module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('section', {
    attrs: {
      "epub:type": "chapter",
      "title": "1. Education for the Common Good in a Diverse World"
    }
  }, [_c('p', {
    staticClass: "h2-cn"
  }, [_c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_17",
      "title": "17"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/00a_Yob_and_Jorgensen_fm_prod0d#ch1"
    }
  }, [_vm._v("1")])])]), _vm._v(" "), _c('p', {
    staticClass: "h2-ct1a"
  }, [_c('b', [_c('a', {
    attrs: {
      "href": "#/text/00a_Yob_and_Jorgensen_fm_prod0d#ch1"
    }
  }, [_vm._v("THERE IS NO OTHER")])])]), _vm._v(" "), _c('p', {
    staticClass: "h2-au"
  }, [_c('a', {
    attrs: {
      "href": "#/text/00a_Yob_and_Jorgensen_fm_prod0d#ch1"
    }
  }, [_vm._v("Iris M. Yob")])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_c('span', {
    staticClass: "dropcap"
  }, [_vm._v("I")]), _c('span', {
    staticClass: "small"
  }, [_vm._v("T IS RARE FOR THE TITLE OF A")]), _vm._v(" publication to end with a question mark, but the 2015 UNESCO document "), _c('i', [_vm._v("Rethinking Education: Towards a Global Common Good?")]), _vm._v(" does just that."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#ch00rfn1",
      "id": "ch00fn1"
    }
  }, [_vm._v("1")])]), _vm._v(" In this case, the question mark captures the central tension in simultaneously thinking globally about the welfare of all while valuing diversity and individual differences. Can one speak of a "), _c('i', [_vm._v("common")]), _vm._v(" good or a single common "), _c('i', [_vm._v("good")]), _vm._v(" when rethinking an education that is appropriate across worldwide contexts, challenges, and opportunities? In this chapter, I will explore whether the tensions can be resolved and, if so, how. I will do so by exploring the apparent conflict between the concept of “the Other,” which recognizes the individuality of each member of the human family, and the concept that there is no Other, which embraces everybody regardless of their differences.")]), _vm._v(" "), _c('p', {
    staticClass: "h3"
  }, [_vm._v("The Other: Humanity Up Close")]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("A significant reality faced by teachers and educational policy makers for every age group is the substantial diversity among learners. Individual readiness, abilities, interests, and needs present themselves to teachers who want to do their best for each student. Then there are personal histories and inherited differences to take into account, which can determine if the match between the individual learner and the official curriculum is a good one or a soul-destroying mismatch that imprisons the student in an educational environment that saps his or her curiosity and discourages active engagement in learning and personal development. Teachers have long recognized these differences, and many experiment with ways to meet the individual needs of each learner in their care.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_18",
      "title": "18"
    }
  }, [_vm._v("Of course, this diversity manifests in more than learning readiness and inherited and cultivated abilities. A recognition of multiculturalism has become standard in referring to diversity in society and in the classroom. Racial and ethnic backgrounds help form our self-understanding, perception of the world, and values and belief systems, as well as shaping how others see us and respond to us. These backgrounds give us language and the words we use to communicate and make meaning. Across our communities, there are others whose skin tone is different and who dress in different clothes, eat different foods, sing different songs, play different instruments, enjoy different games, speak different languages, and worship different deities in different ways and in different sacred places. Add to this mix the range of differences in gender and sexual orientation and identity, age, physical ability and appearance, political affiliation, family background, geographic location, life experiences, loves, hopes, dreams, and fears. Getting our heads around the “booming, buzzing confusion” of the world around us is a formidable task."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#ch00rfn2",
      "id": "ch00fn2"
    }
  }, [_vm._v("2")])]), _vm._v(" It is a task that teachers in both formal and informal educational settings need to be responsive to in ways that promote the well-being and personal development of each individual in their care."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#ch00rfn3",
      "id": "ch00fn3"
    }
  }, [_vm._v("3")])])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The apparent diversity in our communities and educational institutions has compelled us to examine the notions of equality and fairness. Equality demands that everyone has the same opportunities and access to resources. Fairness acknowledges that equality cannot be determined by quantity alone but may mean differences in the kinds of offerings we provide. For instance, a differently abled learner may need a one-on-one caretaker, mentor, or teacher’s aide to participate in classroom exercises with everybody else; a Muslim teenage girl may be better served by a girls-only swim class; the choice of repertoire or performance venue for a school choir may need to be chosen with sensitivity to the different affiliations and belief systems of the choir members and their parents."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#ch00rfn4",
      "id": "ch00fn4"
    }
  }, [_vm._v("4")])]), _vm._v(" Such accommodations may challenge the notion of “equality” if understood rigidly or simplistically; they demand creativity in conception and practice to maximize fairness for all. The fundamental goal of equality and fairness is inclusion. No one is to be left out despite his or her differences, even if inclusion requires accommodations.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Postmodernism shares with multiculturalism a number of key premises: “a valuation of marginality, a suspicion of master discourses, a resistance to empty conventions.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#ch00rfn5",
      "id": "ch00fn5"
    }
  }, [_vm._v("5")])]), _vm._v(" It allows different interpretations of shared facts and even the existence of facts that may have been automatically filtered "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_19",
      "title": "19"
    }
  }, [_vm._v("out by one’s assumptions and beliefs. It does not, however, support propaganda created to convince others of a particular account or ideology, such as “alternative facts” and “fake news,” because they lack evidence, reason, and integrity in any shared system of meaning making. Postmodernism challenges our self-understanding and worldviews by acknowledging the existence of and validating the Other, including even the “nomads” or “schizophrenics” in our society."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#ch00rfn6",
      "id": "ch00fn6"
    }
  }, [_vm._v("6")])]), _vm._v(" For instance, a person may be white and, therefore, mainstream but also a woman, making her a nomad in a man’s world, just as an old person is a nomad in a young person’s world. Someone who is gay, wheelchair-bound, and an immigrant may hold multiple senses of self. When followed through to its logical conclusion, we become increasingly aware that we too are Other—we may be multiple Others within ourselves or may be nomads out of the mainstream. And we are certainly Other to others.")])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Postmodernism has opened the door to new words and concepts as we respond to this growing awareness of the Other. Poststructuralism is one natural companion of postmodernism, with its fundamental premise that the interpretation of texts, how one reads and understands something written by another, or even something enacted, played, or sung, is not necessarily what is intended by the author or performer but is determined by the reader/watcher/listener’s personal history, context, intentions, and values. So, multiple possible interpretations and meanings can be ascribed to every text and act. For teachers, this means that each learner can be seeing and hearing and viscerally experiencing something different in a shared learning event.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Deconstruction is another companion of postmodernism. Its basic premise is that words are not reality but signifiers and therefore notoriously unreliable. Claims made in a modernist mode are taken as certain, universally applicable, and stable; claims seen through the lens of postmodernism can be questioned because they are individually or locally understood and, therefore, changeable and open to interrogation. In other words, what one individual takes as inalienable truth is possibly just one truth among many possible truths at any particular moment in time—or possibly even an untruth for some. Failure to acknowledge the possibility of multiple truths can result in policy actions, teaching approaches, and communal relationships that are inimical and violent to some Others.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In an extreme form, deconstructionist ideas are nihilist; that is, they lead to the conclusion that there can be no certainties. In a less extreme "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_20",
      "title": "20"
    }
  }, [_vm._v("form, deconstruction is a critical approach to what we know. It encourages us to interrogate and problematize key concepts, assumptions, and truisms that we might have taken for granted and that might alienate us from others or at some level do harm to others by violating their very way of thinking and being. This approach can open us to new possibilities and alternative worldviews, or at least to the very real possibility that there are alternatives.")])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Understanding the Other—and there are grave concerns about whether this is even possible—or at least acknowledging to ourselves that there are Others requires us to take a close-up view of the human family. It has given added impetus to the various rights movements: civil rights, women’s rights, LGBT rights, and the rights of the differently abled, for instance. Each of these movements has taken as fundamental that the differences exhibited by individual groups should be protected and included in social policy, access to resources, and fair treatment. “Politically correct” language, or how we talk about the Other, and the inclusiveness and sensitivity to differences that we demonstrate in our discourse has grown out of the same root, no matter how disparaged it may have become.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("When mishandled and incomplete, however, the close-up view can also tempt us into classifying and stereotyping, sorting and dividing, identifying and separating. The rifts between different others can promote wariness and even fear of the Other—fear of losing one’s own identity in a morass of legitimate othernesses, fear of the challenges others might pose to one’s cherished way of being and the sharing of resources that might be required, fear of an implicit requirement to change. Because of these fears, schools may assume the responsibility, tacit or explicit, to move the Other toward being more mainstream and therefore more “acceptable”—to moderate and dominate, rather than include and smooth the sharp edges of exception, to create a sameness (that can end up being dull and monochromatic). So, the close-up view must go deep, pushing through any initial fear and reticence to a fuller acceptance and celebration of the differences within the human family. This is where a humane music education takes place.")]), _vm._v(" "), _c('p', {
    staticClass: "h3"
  }, [_vm._v("There Is No Other: The Wide-Angle View")]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("While the close-up view of humanity is a preferred option for all of us living effectively in today’s world and mandatory for teachers who must respond to differences every day and in every learning event, the wide-angle view is equally compelling. As our view zooms out to encompass increasingly "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_21",
      "title": "21"
    }
  }, [_vm._v("wider sweeps, from individuals to classrooms, communities, nations, and the world, human beings begin to merge into one, differences become fuzzier, and commonalities across all individuals emerge. If we zoom out far enough, we begin to see the common humanness, the shared history and potential futures, and the common ecological system in which that humanity exists, to which it contributes, and by which it is nurtured. The wide-angle view is also a preferred option.")])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The expression “There is no Other” both acknowledges the existence of the Other and simultaneously affirms something more than otherness."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#ch00rfn7",
      "id": "ch00fn7"
    }
  }, [_vm._v("7")])]), _vm._v(" It is an iteration of much moral philosophy and the fundamental ethic of the world’s religions: “Do unto others as you would have them do unto you.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#ch00rfn8",
      "id": "ch00fn8"
    }
  }, [_vm._v("8")])]), _vm._v(" The Golden Rule, as the statement is best known, invites one to see the Other in oneself and oneself in the Other. It blurs the differences that might exist among us as human beings and encourages us to reach for a common humanity that collectively knows pains, fears, needs, hopes, aspirations, and dreams. This goes beyond empathy for others to embrace a oneness with others.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("With public discourse that seems dominated by extremists on all sides and entrenched partisanship that stalls politics and governance, healing the rifts among and between people calls for this wider view. In pointing out how we are different from others, we can also be aware that we are in the same human family. Mahatma Gandhi is on record as saying, “I am a Muslim, and a Christian, and a Buddhist, and a Jew.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#ch00rfn9",
      "id": "ch00fn9"
    }
  }, [_vm._v("9")])]), _vm._v(" Reminiscent of this are the banners waved at the time of the terrorist attack on the "), _c('i', [_vm._v("Charlie Hebdo")]), _vm._v(" offices in Paris that read “Je suis Charlie” and the tweet of former US secretary of state Madeline Albright that said “I was raised Catholic, became Episcopalian and found out later my family was Jewish. I stand ready to register as Muslim in #solidarity.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#ch00rfn10",
      "id": "ch00fn10"
    }
  }, [_vm._v("10")])]), _vm._v(" These Shylockian pronouncements indicate our togetherness despite our differences—and for good or ill, as Shakespeare’s character illustrates."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#ch00rfn11",
      "id": "ch00fn11"
    }
  }, [_vm._v("11")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The stance of “no Other” is deeply connected with morality. When we allow some members of the human family to become invisible to us and to exist outside our imaginative and empathetic world because they speak a different language, are a different color or socioeconomic status, or possess different intellectual or physical abilities, we are treating those people immorally. John Dewey makes this point in "), _c('i', [_vm._v("A Common Faith")]), _vm._v(", when he pictures “all in the same boat traversing the same turbulent ocean.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#ch00rfn12",
      "id": "ch00fn12"
    }
  }, [_vm._v("12")])]), _vm._v(" This is also the central argument of Raimond Gaita’s "), _c('i', [_vm._v("A Common Humanity: Thinking "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_22",
      "title": "22"
    }
  }, [_vm._v("about Love and Truth and Justice")])]), _vm._v(", although Gaita expresses this same idea more positively: “Treat me as a human being, fully as your equal, without condescension—that demand (or plea), whether it is made by women to men or by blacks to whites, is a demand or a plea for justice.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#ch00rfn13",
      "id": "ch00fn13"
    }
  }, [_vm._v("13")])]), _vm._v(" This succinct statement goes beyond simple justice, however; Gaita opens the possibility of love for others without reservation. This is where he describes a “goodness beyond virtue” for it goes beyond fairness and equal rights to respect for and acceptance of the full humanity of all others without condescension."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#ch00rfn14",
      "id": "ch00fn14"
    }
  }, [_vm._v("14")])]), _vm._v(" For him, it is an unconditional acceptance of the “preciousness of others” that disregards anything we might feel uncomfortable with or dislike. It involves loving others “beyond reason, beyond merit and beyond what moralisers might say about them.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#ch00rfn15",
      "id": "ch00fn15"
    }
  }, [_vm._v("15")])]), _vm._v(" His stance includes, for example, even a Hitler or an Eichmann, because Gaita accepts as fundamental the interconnectedness and interdependence of all human beings. This allows him to conclude that as there is goodness beyond virtue, so there is “justice beyond fairness,” one that deeply accepts and is built on the full and shared humanity of all."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#ch00rfn16",
      "id": "ch00fn16"
    }
  }, [_vm._v("16")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Gaita echoes a similar argument made earlier by Philip H. Phenix concerning the essential morality inherent in recognizing our shared humanity. This morality is bound to the notion of democracy in which all are equal. Individuals are equal, Phenix elaborates, not because they have the same “abilities, wants, interests, needs, qualities, or circumstances” but because they are “human, mortal, possessed of body and mind.” Consequently, a single “standard of worth” applies to all."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#ch00rfn17",
      "id": "ch00fn17"
    }
  }, [_vm._v("17")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Systems theory gives us an important perspective when we take the wide-angle view of humanity, for it sees the whole human family and its environment as connected and interrelated. This web-of-life or ecological view brings every individual into the picture in a dynamic way, for the bonds that tie us together make us one; whatever impacts one has a ripple effect throughout the whole. It is easy to see this on so many levels: when a tsunami in Japan brings flotsam to the shores of North America; when greenhouse gases contribute to melting icecaps and shrinking glaciers; when a monetary policy in one country sends a shiver through economies worldwide; and when poverty or greed drives people to cut down a rainforest, upsetting the water cycle and contributing to drought in other parts of the world. On a social level, an authoritarian and militaristic regime can raise the threat of nuclear armament so that nations begin to fear other nations, perceived slights can enrage terrorists, unsanitary and impoverished "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_23",
      "title": "23"
    }
  }, [_vm._v("conditions can start a pandemic, ignorance and powerlessness can make people vulnerable to exploitation by others, and tyranny can produce waves of refugees desperate enough to climb into flimsy boats and cross treacherous seas to safer havens. When one child is abandoned to street life or one family is homeless and hungry or one enemy combatant is tortured, we are all diminished.")])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("On the other hand, when the knowledge gained in a literate society is used in the service of all, individuals reap the dividends that come from an improved economy, better access to health services, government stability, and improved status for women and minorities. When we nurture the best in ourselves and our children locally, we contribute to humanity as a whole. In an important sense, despite the overwhelming magnitude of the problems facing this planet and its inhabitants, systems thinking can give us courage and resolve.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Many forces impinge on us today, threatening to drive us apart. Populism and nationalistic ideologies would build walls, real and ideological, around groups who feel rightly or wrongly beleaguered and endangered, isolating people from each other and severing bonds of caring and mutuality across borders and boundaries. Hyperpartisanship divides liberals from conservatives, capturing people in echo chambers, where they hear only their own kind talking, making cooperation for the common good almost impossible. The entitled, the rich, the exploiters, the “one-percenters” are accused of losing touch with the struggles of ordinary people, the poor, the exploited, the other 99 percent. The secure and comfortable are afraid of refugees, often without any good reason and without offering positive solutions to their plight. Economies are pitted against each other, so they cannot agree on responses to carbon emissions, fair trade practices, or worker safety and protection standards. Racism, misogyny, and homophobia too often go largely unchallenged in public and private discourse.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("All this is happening at a time when humanity needs to unite and find realistic, long-lasting solutions to environmental degradation and the increasing gaps between rich and poor people, communities, and nations. Violence, both local and international, calls for collective action, as does exploitation of women, children, and the vulnerable. Educational opportunities, health care and safe living conditions, preservation and support of the family, and peace at home and abroad cannot be accomplished by a single individual or group of individuals. These are concerns for all human "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_24",
      "title": "24"
    }
  }, [_vm._v("beings, and they require the collective effort of all to see them accomplished. This is where the cutting edge of a music education for the common good can contribute.")])]), _vm._v(" "), _c('p', {
    staticClass: "h3"
  }, [_vm._v("The "), _c('i', [_vm._v("Common")]), _vm._v(" Good and the Common "), _c('i', [_vm._v("Good")])]), _vm._v(" "), _c('p', {
    staticClass: "noindent"
  }, [_vm._v("More than the UNESCO reports preceding it, "), _c('i', [_vm._v("Rethinking Education")]), _vm._v(" expresses the force of the tension between the Other and no Other. Its humanistic vision encompasses both, for its basic principles include, on the one hand, “equal rights and social justice” and “respect for cultural diversity,” which represent a response to the Other, and on the other hand, “respect for life and human dignity” and “international solidarity and shared responsibility,” which point to a shared humanity that operates as though there is no Other."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#ch00rfn18",
      "id": "ch00fn18"
    }
  }, [_vm._v("18")])]), _vm._v(" This report accepts with little modification the tensions identified in the 1996 Delors report from UNESCO, including that “between the global and the local; the universal and the particular,” which we have identified as the wide-angle view and the close-up view of humanity, respectively."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#ch00rfn19",
      "id": "ch00fn19"
    }
  }, [_vm._v("19")])]), _vm._v(" If anything is favored in "), _c('i', [_vm._v("Rethinking Education")]), _vm._v(", it may be the wide-angle view that there is no Other, given what the writers identify as “the central concern” of the world: sustainability of life on the planet."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#ch00rfn20",
      "id": "ch00fn20"
    }
  }, [_vm._v("20")])]), _vm._v(" Even in this discussion, however, they note that human rights campaigns, awareness of growing disparities in wealth, reports of escalating violence and disconnectedness and rising vulnerabilities among people, and a greater awareness of diverse worldviews draw our attention back to the plight of the Other.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("We find here the beginnings of a resolution of the tension between the Other and no Other, for maybe they are inseparable. What is common about the common good, as far as education is concerned, is first stated in the conclusion to the report’s second chapter. Announcing that a sustainable existence must acknowledge the variety of social, economic, and environmental dimensions of human development, the writers state that “an empowering education is one that builds the human resources we need to be productive, to continue to learn, to solve problems, to be creative, and to live together and with nature in peace and harmony.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#ch00rfn21",
      "id": "ch00fn21"
    }
  }, [_vm._v("21")])]), _vm._v(" Nurturing the development of each individual human being across the life span, wherever that individual is located and whatever his or her circumstances, is what is required to live meaningful lives, in dignity, and this will contribute to the “collective quest of well-being.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#ch00rfn22",
      "id": "ch00fn22"
    }
  }, [_vm._v("22")])]), _vm._v(" There is a common "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_25",
      "title": "25"
    }
  }, [_vm._v("purpose implied here, but not necessarily a common means and method for achieving it.")])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("The distinction between the Other and no Other may tempt us into a false dichotomy. However, the Other exists in our shared humanity, and the no Other includes those who are Other. I saw a metaphor for this when visiting the archaeological site of the terracotta warriors near Xi’an, China. The scale of the various pits where the figures have been unearthed is astonishing (like the scale of so many things in China): row after row, column after column, in pit after pit of standing life-size soldier figures stretched out before us. But even more astonishing is what you see when you look more closely: each figure is unique as evidenced by the variety of beards, moustaches, hairstyles, buttons on jackets, sleeve styles, boots, buckles, the placement of the hands, and the tilt of the head. Even the horse figures stand differently, wear different saddles and bridles, and have their own distinctive tail and mane plaits—such diversity in military figures where uniforms and uniformity are ideals! When you take a wider view, however, these individual soldiers make up a single army, unified in their dignity and purpose to serve the emperor. Their differences make up the whole.")]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("This fusion of the Other and the no Other is also illustrated by several writers in this collection. We see it, for instance, in the purposeful variety of music cultures that make up the New Canadian Global Music Orchestra in Toronto; in the multiple types of voice production, representing various singing cultures, learned and practiced together in the same voice class; in the elimination of differentiating roles for music educators in the Boston area engaged with prisoners, the differently abled, or people embedded in a different ethnicity as they engage in music making together; in Hungary with severely disabled young people performing with a community of other musicians; and in the hospitality shown the transgendered Other in a college choir."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#ch00rfn23",
      "id": "ch00fn23"
    }
  }, [_vm._v("23")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("In these examples, the Other is both not present and present. These principles apply equally to all lifelong learners—all lives are respected; all have equal rights and are entitled to what is fair and just; cultural diversity everywhere is respected, and all participate and share in a community of human music makers and music learners. Yet, throughout, the Other does not lose his or her otherness. This is not a case of either-or, where our attention to the Other or the no Other is taking its turn on center stage or waiting in the wings for its turn. It is essentially a case of “this with that,” where "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_26",
      "title": "26"
    }
  }, [_vm._v("both are always center stage."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#ch00rfn24",
      "id": "ch00fn24"
    }
  }, [_vm._v("24")])]), _vm._v(" The writers of "), _c('i', [_vm._v("Rethinking Education")]), _vm._v(" express this central idea in these words: “We must recognize the diversity of lived realities while reaffirming a common core of universal values.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#ch00rfn25",
      "id": "ch00fn25"
    }
  }, [_vm._v("25")])]), _vm._v(" In other words, we validate the Other by cultivating the humanness that connects us all as though there is no Other.")])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("This basic resolution also helps answer the question, what is good about the common "), _c('i', [_vm._v("good")]), _vm._v("? The basic good is “sustainable human and social development.” It is a goal of education that takes the wide view of humanity. At the same time, it must be “both equitable and viable.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#ch00rfn26",
      "id": "ch00fn26"
    }
  }, [_vm._v("26")])]), _vm._v(" Later in "), _c('i', [_vm._v("Rethinking Education")]), _vm._v(", the writers speak of “common goods”: “the goodness of the life that humans hold in common,” “defined with regard to the diversity of contexts and conceptions of well-being and common life,” undertaken as a “participatory process” involving “shared action . . . intrinsic . . . to the good itself.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#ch00rfn27",
      "id": "ch00fn27"
    }
  }, [_vm._v("27")])]), _vm._v(" The common good can be best, or perhaps only, understood when we focus on the human being—not some idealized, theoretical being, but real people living in actual contexts in our time, as the 1972 Faure report from UNESCO report indicates."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#ch00rfn28",
      "id": "ch00fn28"
    }
  }, [_vm._v("28")])]), _vm._v(" This means seeking the development of the potential of humans in all their capacities for good, as the Delors report proposes."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#ch00rfn29",
      "id": "ch00fn29"
    }
  }, [_vm._v("29")])]), _vm._v(" It is bound up with “the right to meaningful and relevant learning,” which is explained in "), _c('i', [_vm._v("Rethinking Education")]), _vm._v(" by both the close-up view of humanity—that there are “many different ways of defining the quality of life”—and the wide-angle view—that education and knowledge are common to all “as a collective social endeavor.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/text/#ch00rfn30",
      "id": "ch00fn30"
    }
  }, [_vm._v("30")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent"
  }, [_vm._v("Formal and informal educators, policy makers, curriculum builders, administrators, teachers, and community music makers are called on to nurture the human family in all its diversity. Being humane is understood not simply as being kind to others but more rigorously and radically as acknowledging the essential value of each individual with common human needs and to whom we are inseparably bound both now and in the future that we create together. It is a way of thinking and being that not only promotes the “good life” but nurtures the goodness in the life humans share. The way forward is by recognizing and validating the Other, the individual who is different from us, and at the same time, fully appreciating the commonalities and the wholeness of humankind, as if there is no Other.")]), _vm._v(" "), _c('p', {
    staticClass: "noindenta"
  }, [_vm._v("IRIS M. YOB is Faculty Emerita and Contributing Faculty Member in the Richard W. Riley College of Education and Leadership at Walden University, Minnesota.")]), _vm._v(" "), _c('p', {
    staticClass: "h3"
  }, [_c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_27",
      "title": "27"
    }
  }, [_vm._v("Notes")])]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "ch00rfn1"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#ch00fn1"
    }
  }, [_vm._v("1")]), _vm._v(". UNESCO, "), _c('i', [_vm._v("Rethinking Education: Towards a Global Common Good?")]), _vm._v(" (Paris: UNESCO, 2015), "), _c('a', {
    attrs: {
      "href": "http://unesdoc.unesco.org/images/0023/002325/232555e.pdf",
      "target": "_blank"
    }
  }, [_vm._v("http://unesdoc.unesco.org/images/0023/002325/232555e.pdf")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "ch00rfn2"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#ch00fn2"
    }
  }, [_vm._v("2")]), _vm._v(". Here, I am borrowing a phrase that William James used to describe the sensory stimuli we must learn to process, one that seems just as apt when describing the social stimuli we must learn to process. William James, "), _c('i', [_vm._v("Principles of Psychology")]), _vm._v(" (1890; Cambridge, MA: Harvard University Press, 1983), 462.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "ch00rfn3"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#ch00fn3"
    }
  }, [_vm._v("3")]), _vm._v(". See, for instance, some of the work of one of the most creative music educators, June Boyce Tillman, who sets up choirs for dementia patients and people who self-identify with the statement “I can’t sing” and stages musical productions that bring in every age, religious group, and ability level. See June Boyce Tillman, “The Dignity of Difference” in "), _c('i', [_vm._v("Stories of the Great Turning")]), _vm._v(", ed. Peter Reason and Melanie Newman (Bristol: Vala, 2013), 169–177; June Boyce Tillman, “Music and Well-Being—Music as Integrative Experience,” "), _c('i', [_vm._v("Journal of Urban Culture Research Art Access and Advocacy—Promoting Creativity and Innovation for the Development of Participatory and Happy Communities")]), _vm._v(" 7 (2013): 48–71.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "ch00rfn4"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#ch00fn4"
    }
  }, [_vm._v("4")]), _vm._v(". See William Perrine, “"), _c('i', [_vm._v("Bauchman vs West High School")]), _vm._v(" Revisited,” "), _c('i', [_vm._v("Philosophy of Music Education Review")]), _vm._v(" 25, no. 2 (Fall 2017): 200–204.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "ch00rfn5"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#ch00fn5"
    }
  }, [_vm._v("5")]), _vm._v(". Rafael Perez-Torres, “Nomads and Migrants: Negotiating a Multicultural Postmodernism,” in "), _c('i', [_vm._v("Cultural Critique")]), _vm._v(" (Winter 1993–1994), 161, "), _c('a', {
    attrs: {
      "href": "https://www.peacepalacelibrary.nl/ebooks/files/Nomads-and-Migrants-Negotiating-Postmodernism-Perez-Torres.pdf",
      "target": "_blank"
    }
  }, [_vm._v("https://www.peacepalacelibrary.nl/ebooks/files/Nomads-and-Migrants-Negotiating-Postmodernism-Perez-Torres.pdf")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "ch00rfn6"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#ch00fn6"
    }
  }, [_vm._v("6")]), _vm._v(". Gilles Deleuze and Felix Guattari, "), _c('i', [_vm._v("A Thousand Plateaus: Capitalism and Schizophrenia")]), _vm._v(", trans. Brian Massumi (Minneapolis: University of Minnesota Press, 1987), celebrate these terms, which are meant to describe not a pathological condition but rather the act of putting oneself outside the “normal” to allow a creative and more just engagement with the world and others. I am somewhat hesitant to use the term "), _c('i', [_vm._v("schizophrenic")]), _vm._v(" even as a quote because applying "), _c('i', [_vm._v("schizophrenia")]), _vm._v(" to an individual who is not mentally ill but is culturally nonconforming perpetuates a demeaning stereotype (“That’s crazy!”) and does not promote creativity, given that the term “schizophrenic” is still a pejorative in the general population.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "ch00rfn7"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#ch00fn7"
    }
  }, [_vm._v("7")]), _vm._v(". For introducing me to this expression, I am grateful to Jan Hively, who has been an activist for good causes all her life and is one of the wise women I am better for knowing.")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "ch00rfn8"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#ch00fn8"
    }
  }, [_vm._v("8")]), _vm._v(". Comparative statements from religious groups can be found at the following websites: “‘The Golden Rule’ a.k.a. Ethics of Reciprocity, Part I,” Religious Tolerance, accessed January 17, 2017, "), _c('a', {
    attrs: {
      "href": "http://www.religioustolerance.org/reciproc2.htm",
      "target": "_blank"
    }
  }, [_vm._v("http://www.religioustolerance.org/reciproc2.htm")]), _vm._v("; and “‘The Golden Rule’ a.k.a. Ethics of Reciprocity, Part II,” Religious Tolerance, accessed January 17, 2017, "), _c('a', {
    attrs: {
      "href": "http://www.religioustolerance.org/reciproc3.htm",
      "target": "_blank"
    }
  }, [_vm._v("http://www.religioustolerance.org/reciproc3.htm")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "note",
    attrs: {
      "id": "ch00rfn9"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#ch00fn9"
    }
  }, [_vm._v("9")]), _vm._v(". See, for instance, “Mahatma Gandhi,” Goodreads, accessed July 7, 2019, "), _c('a', {
    attrs: {
      "href": "http://www.goodreads.com/quotes/361107-yes-i-am-i-am-also-a-muslim-a-christian",
      "target": "_blank"
    }
  }, [_vm._v("http://www.goodreads.com/quotes/361107-yes-i-am-i-am-also-a-muslim-a-christian")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "note1",
    attrs: {
      "id": "ch00rfn10"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#ch00fn10"
    }
  }, [_vm._v("10")]), _vm._v(". Madeleine Albright (@madeleine), Twitter, January 25, 2017, 1:18 p.m.")]), _vm._v(" "), _c('p', {
    staticClass: "note1",
    attrs: {
      "id": "ch00rfn11"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#ch00fn11"
    }
  }, [_vm._v("11")]), _vm._v(". Shylock, who was different from other merchants in Venice because he was a Jew, claims a common humanity with his Christian counterparts: “Doesn’t a Jew have eyes? Doesn’t a Jew have hands, bodily organs, a human shape, five senses, feelings, and passions? Doesn’t a Jew eat the same food, get hurt with the same weapons, get sick with the same diseases, get healed by the same medicine, and warm up in summer and cool off in winter "), _c('span', {
    staticClass: "right_1",
    attrs: {
      "epub:type": "pagebreak",
      "id": "page_28",
      "title": "28"
    }
  }, [_vm._v("just like a Christian? If you prick us with a pin, don’t we bleed? If you tickle us, don’t we laugh? If you poison us, don’t we die?”")])]), _vm._v(" "), _c('p', {
    staticClass: "note1a"
  }, [_vm._v("He also reminds us that this commonness is for good or ill: “And if you treat us badly, won’t we try to get revenge? If we’re like you in everything else, we’ll resemble you in that respect.” William Shakespeare, "), _c('i', [_vm._v("Merchant of Venice")]), _vm._v(", 3.1.52–58. So, while we can joyfully claim our oneness with the human family, like all families, there are dark spots and shady implications to be aware of as well.")]), _vm._v(" "), _c('p', {
    staticClass: "note1",
    attrs: {
      "id": "ch00rfn12"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#ch00fn12"
    }
  }, [_vm._v("12")]), _vm._v(". John Dewey, "), _c('i', [_vm._v("A Common Faith")]), _vm._v(" (New Haven: Yale University Press, 1934), 84.")]), _vm._v(" "), _c('p', {
    staticClass: "note1",
    attrs: {
      "id": "ch00rfn13"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#ch00fn13"
    }
  }, [_vm._v("13")]), _vm._v(". Raimond Gaita, "), _c('i', [_vm._v("A Common Humanity: Thinking about Love and Truth and Justice")]), _vm._v(" (London: Routledge, 1998), xx.")]), _vm._v(" "), _c('p', {
    staticClass: "note1",
    attrs: {
      "id": "ch00rfn14"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#ch00fn14"
    }
  }, [_vm._v("14")]), _vm._v(". Ibid., 17–27.")]), _vm._v(" "), _c('p', {
    staticClass: "note1",
    attrs: {
      "id": "ch00rfn15"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#ch00fn15"
    }
  }, [_vm._v("15")]), _vm._v(". Ibid., 27.")]), _vm._v(" "), _c('p', {
    staticClass: "note1",
    attrs: {
      "id": "ch00rfn16"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#ch00fn16"
    }
  }, [_vm._v("16")]), _vm._v(". Ibid., 73–85.")]), _vm._v(" "), _c('p', {
    staticClass: "note1",
    attrs: {
      "id": "ch00rfn17"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#ch00fn17"
    }
  }, [_vm._v("17")]), _vm._v(". Philip H. Phenix, "), _c('i', [_vm._v("Education and the Common Good")]), _vm._v(" (New York: Harper, 1961), 7.")]), _vm._v(" "), _c('p', {
    staticClass: "note1",
    attrs: {
      "id": "ch00rfn18"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#ch00fn18"
    }
  }, [_vm._v("18")]), _vm._v(". UNESCO, "), _c('i', [_vm._v("Rethinking Education")]), _vm._v(", 14.")]), _vm._v(" "), _c('p', {
    staticClass: "note1",
    attrs: {
      "id": "ch00rfn19"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#ch00fn19"
    }
  }, [_vm._v("19")]), _vm._v(". UNESCO, "), _c('i', [_vm._v("Learning: The Treasure Within")]), _vm._v(" (Paris, UNESCO, 1996), 20, "), _c('a', {
    attrs: {
      "href": "http://unesdoc.unesco.org/images/0010/001095/109590eo.pdf",
      "target": "_blank"
    }
  }, [_vm._v("http://unesdoc.unesco.org/images/0010/001095/109590eo.pdf")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "note1",
    attrs: {
      "id": "ch00rfn20"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#ch00fn20"
    }
  }, [_vm._v("20")]), _vm._v(". UNESCO, "), _c('i', [_vm._v("Rethinking Education")]), _vm._v(", 19–33.")]), _vm._v(" "), _c('p', {
    staticClass: "note1",
    attrs: {
      "id": "ch00rfn21"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#ch00fn21"
    }
  }, [_vm._v("21")]), _vm._v(". Ibid., 32.")]), _vm._v(" "), _c('p', {
    staticClass: "note1",
    attrs: {
      "id": "ch00rfn22"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#ch00fn22"
    }
  }, [_vm._v("22")]), _vm._v(". Ibid., 33.")]), _vm._v(" "), _c('p', {
    staticClass: "note1",
    attrs: {
      "id": "ch00rfn23"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#ch00fn23"
    }
  }, [_vm._v("23")]), _vm._v(". In this collection, please see Deanne Bogdan, “Dissociation/Reintegration of Literary/Musical Sensibility”; Emily Good-Perkins, “Rethinking Vocal Education as a Means to Encourage Positive Identity Development in Adolescents”; Emily Howe, André de Quadros, Andrew Clark, and Kinh T. Vu, “The Tuning of the Music Educator: A Pedagogy of the ‘Common Good’ for the Twenty-First Century”; Luca Tiszai, “Friendship, Solidarity, and Mutuality Discovered in Music”; and Jacob Axel Berglin and Thomas Murphy O’Hara, “Working with Transgendered Students as a Humane Act: Hospitality and the Other.”")]), _vm._v(" "), _c('p', {
    staticClass: "note1",
    attrs: {
      "id": "ch00rfn24"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#ch00fn24"
    }
  }, [_vm._v("24")]), _vm._v(". Estelle R. Jorgensen, “This with That: A Dialectical Approach to Teaching for Musical Imagination,” "), _c('i', [_vm._v("Journal of Aesthetic Education")]), _vm._v(" 40, no. 4 (Winter 2006): 1–20; Estelle R. Jorgensen, “A Dialectic View of Theory and Practice,” "), _c('i', [_vm._v("Journal of Research in Music Education")]), _vm._v(" 49, no. 4 (2001): 343–359.")]), _vm._v(" "), _c('p', {
    staticClass: "note1",
    attrs: {
      "id": "ch00rfn25"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#ch00fn25"
    }
  }, [_vm._v("25")]), _vm._v(". UNESCO, "), _c('i', [_vm._v("Rethinking Education")]), _vm._v(", 29.")]), _vm._v(" "), _c('p', {
    staticClass: "note1",
    attrs: {
      "id": "ch00rfn26"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#ch00fn26"
    }
  }, [_vm._v("26")]), _vm._v(". Ibid., 31.")]), _vm._v(" "), _c('p', {
    staticClass: "note1",
    attrs: {
      "id": "ch00rfn27"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#ch00fn27"
    }
  }, [_vm._v("27")]), _vm._v(". Ibid., 78.")]), _vm._v(" "), _c('p', {
    staticClass: "note1",
    attrs: {
      "id": "ch00rfn28"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#ch00fn28"
    }
  }, [_vm._v("28")]), _vm._v(". UNESCO, "), _c('i', [_vm._v("Learning to Be: The World of Education Today and Tomorrow")]), _vm._v(" (Paris: UNESCO, 1972), "), _c('a', {
    attrs: {
      "href": "http://unesdoc.unesco.org/images/0000/000018/001801e.pdf",
      "target": "_blank"
    }
  }, [_vm._v("http://unesdoc.unesco.org/images/0000/000018/001801e.pdf")]), _vm._v(" (also known as the Faure report).")]), _vm._v(" "), _c('p', {
    staticClass: "note1",
    attrs: {
      "id": "ch00rfn29"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#ch00fn29"
    }
  }, [_vm._v("29")]), _vm._v(". UNESCO, Delors report.")]), _vm._v(" "), _c('p', {
    staticClass: "note1",
    attrs: {
      "id": "ch00rfn30"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/text/#ch00fn30"
    }
  }, [_vm._v("30")]), _vm._v(". UNESCO, "), _c('i', [_vm._v("Rethinking Education")]), _vm._v(", 80.")])])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-4a8efd0e","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/text/01_Yob_and_Jorgensen_ch1_prod.vue
// module id = 84
// module chunks = 0